import * as codeModeStore from "./store";
import { requestDeckorateTab } from "../../../../integrations/tools/deckorate";
import { logError, scrubMessage } from "../../../telemetry";
import { focusCodeModeCatcher } from "./focusCatcher";

/** Must match `keyboardShortcuts[].shortcuts[].actionId` in manifest.json
 * (and `actions[].id` in public/shortcuts.json, for the XML manifests)
 * exactly — a mismatch fails silently, with Office simply never finding a
 * handler to call. */
const LEADER_ACTION_ID = "codeModeLeader";

/** Must match the `key.default` declared alongside LEADER_ACTION_ID in
 * manifest.json — compared against what Office reports back, so a manifest
 * that says one thing while PowerPoint believes another is visible rather
 * than silent. */
const EXPECTED_LEADER_KEY = "Alt+Shift+K";

/**
 * Compares two key combinations as unordered sets of keys.
 *
 * Office normalises modifier order when it reports a binding back:
 * `getShortcuts()` answers `Shift+Alt+K` for a manifest that declares
 * `Alt+Shift+K`. Comparing the strings directly reported a correctly-bound
 * leader as unbound, which is worse than no diagnostic at all — it points
 * whoever is debugging at the manifest when the manifest is fine.
 */
function isSameCombination(a: string, b: string): boolean {
  const keys = (combination: string) =>
    combination
      .toLowerCase()
      .split("+")
      .map((key) => key.trim())
      .sort((a, b) => a.localeCompare(b))
      .join("+");
  return keys(a) === keys(b);
}

/** Brings the shared-runtime pane forward when the leader is pressed from the
 * slide. Already-visible or an unsupported host both resolve as a no-op —
 * either way the shared runtime is already running, so code mode can arm. */
async function bringPaneForward(): Promise<void> {
  try {
    await Office.addin.showAsTaskpane();
  } catch {
    /* already visible, or host doesn't support the API */
  }
}

/**
 * Registers the single Code Mode leader handler — call once from main.tsx's
 * Office.onReady, PowerPoint only. This is the "reach the add-in from the
 * slide" half of the design (TR-2): it brings the pane forward and arms the
 * accumulator with an empty buffer. Every letter after that is an ordinary
 * bare keystroke, captured in-pane by codeMode/store.ts through
 * DeckorateModule's keydown handling (useCodeMode).
 *
 * One shortcut, not one per starting letter: an Office keyboard shortcut is
 * claimed by PowerPoint natively, before the keystroke reaches the WebView,
 * so declaring `Ctrl+Alt+<letter>` per code letter silently killed the
 * Deckorate nav shortcuts sharing those letters (Ctrl+Alt+D meant Design Hub
 * could never open). A single leader on its own combination collides with
 * nothing and leaves every letter free for codes.
 */
/**
 * Asks Office which key combination is actually bound to the leader action,
 * and reports it when the answer isn't Alt+Shift+K.
 *
 * This is the only way to tell "PowerPoint never registered the shortcut"
 * apart from "it registered but keyboard focus never reached the pane" — the
 * two failure modes look identical from the user's side (nothing happens),
 * and the difference decides whether the fix is a re-sideload or nothing at
 * all. `getShortcuts` returns null for an action whose combination the user
 * reassigned in a conflict prompt, which is the case worth catching: it
 * survives re-sideloads and is only cleared by PowerPoint's "Reset Office
 * Add-ins shortcut preferences" command.
 *
 * Guarded end to end: the API needs the KeyboardShortcuts requirement set,
 * which older builds lack, and a diagnostic must never break registration.
 */
function reportLeaderBinding(): void {
  try {
    const shortcuts = Office.actions.getShortcuts?.();
    if (!shortcuts) return;
    void shortcuts
      .then((bindings) => {
        // TEMPORARY DEBUG (AIVA-7971) — remove before merging.
        console.log("[codemode] Office.actions.getShortcuts() ->", bindings);
        const bound = (bindings as Record<string, string | null>)[LEADER_ACTION_ID];
        if (typeof bound === "string" && isSameCombination(bound, EXPECTED_LEADER_KEY)) return;
        logError("error.office-js", {
          type: "CodeModeLeaderUnbound",
          scrubbedMessage: scrubMessage(
            bound === null || bound === undefined
              ? `${LEADER_ACTION_ID} is not bound — re-sideload, then run "Reset Office Add-ins shortcut preferences"`
              : `${LEADER_ACTION_ID} is bound to ${bound}, expected ${EXPECTED_LEADER_KEY}`,
          ),
        });
      })
      .catch(() => {
        /* unsupported build — nothing to report */
      });
  } catch {
    /* Office.actions.getShortcuts absent on this build */
  }
}

export function registerCodeModeCommands(): void {
  reportLeaderBinding();
  // TEMPORARY DEBUG (AIVA-7971) — remove before merging.
  console.log("[codemode] associating", LEADER_ACTION_ID, "host:", Office.context?.host);
  Office.actions.associate(LEADER_ACTION_ID, (event: Office.AddinCommands.Event) => {
    // TEMPORARY DEBUG (AIVA-7971) — remove before merging.
    // This line is the whole diagnosis: it prints ONLY when Office itself
    // dispatches the shortcut, which is the path that works from the slide.
    // Leader pressed with the slide focused and nothing printed here means
    // the manifest never registered the shortcut.
    console.log("[codemode] OFFICE ACTION FIRED (slide-side path)", { host: Office.context?.host });
    // ExtendedOverrides is manifest-scoped, and manifest-ppt-word.xml covers
    // Word as well as PowerPoint, so Word reserves the leader too. Complete
    // without acting rather than leaving the action unhandled — Code Mode has
    // no meaning outside PowerPoint (there is no Deckorate tab in Word).
    if (Office.context.host !== Office.HostType.PowerPoint) {
      event.completed();
      return;
    }
    void bringPaneForward().finally(() => {
      // Bringing the pane forward isn't enough on its own: the keydown
      // listener that reads codes lives in DeckorateModule, which TaskPane
      // only mounts while the Deckorate tab is active. Land on Chat and the
      // mode would arm with nothing listening, so switch tabs first.
      requestDeckorateTab();
      focusCodeModeCatcher();
      codeModeStore.arm();
      event.completed();
    });
  });
}
